import { dao } from '@platform/dao';
import { titleBatchOf } from '@platform/core/lib/recordTitles';
import type {
  IBulkRequest, IBulkResult, IRankRequest, IRecord, IRecordBatch, IRecordPage, IRecordStats, IRecordTitleBatch, IRecordTypeLinkPage, IRecordWrite, IStatBucket,
  ITransitionRequest,
} from '@platform/core/types';
import type { IFacetsArg } from './args/IFacetsArg';
import type { IIdArg } from './args/IIdArg';
import type { ILinksArg } from './args/ILinksArg';
import type { IListAllArg } from './args/IListAllArg';
import type { IListArg } from './args/IListArg';
import type { IRecordTitlesArg } from './args/IRecordTitlesArg';
import type { IWriteArg } from './args/IWriteArg';
import { baseApi, listTag, recordTag, run, statsTag } from './baseApi';
import { myWorkTag } from './myWorkTag';

/**
 * Списки й записи будь-якого типу; мутації інвалідовують список, статистику й запис. Зміна, перехід, видалення й масові дії ще й
 * інвалідовують зв'язки типів (Links/LIST): назви, статуси й видалення записів видно в конвертах зв'язків (recordTypeLinks).
 */
export const recordsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listRecords: b.query<IRecordPage, IListArg>({
      queryFn: ({ ws, rt, params }) => run(() => dao.records.list(ws, rt, params)),
      providesTags: (r, _e, { rt }) => [listTag(rt), ...(r?.items.map((i) => recordTag(rt, i.id)) ?? [])],
    }),
    listAllRecords: b.query<IRecordBatch, IListAllArg>({
      queryFn: ({ ws, rt, params, cap }) => run(() => dao.records.listAll(ws, rt, params, cap)),
      providesTags: (_r, _e, { rt }) => [listTag(rt)],
    }),
    /**
     * Назви записів кількох типів одним запитом (підписи полів-посилань у списку: тип → id → назва): усі записи кожного типу посторінково
     * до стелі 1000, як listAllRecords; тип, який не вдалося прочитати, дає порожній словник (клітинки покажуть значення як є).
     * Кешується зі списковими тегами цих типів, тож зміна назви запису оновлює підписи.
     */
    recordTitles: b.query<Record<string, IRecordTitleBatch>, IRecordTitlesArg>({
      queryFn: ({ ws, types }) => run(async () => {
        const settled = await Promise.allSettled(types.map((rt) => dao.records.listAll(ws, rt)));
        return Object.fromEntries(settled.map((r, i) => [types[i], r.status === 'fulfilled' ? titleBatchOf(r.value) : { titles: {}, capped: false }]));
      }),
      providesTags: (_r, _e, { types }) => types.map(listTag),
    }),
    recordTypeLinks: b.query<IRecordTypeLinkPage, ILinksArg>({
      queryFn: ({ ws, rt, other }) => run(() => dao.records.links(ws, rt, other)),
      providesTags: () => [{ type: 'Links', id: 'LIST' }],
    }),
    getRecord: b.query<IRecord, IIdArg>({
      queryFn: ({ ws, rt, id }) => run(() => dao.records.get(ws, rt, id)),
      providesTags: (_r, _e, { rt, id }) => [recordTag(rt, id)],
    }),
    getStats: b.query<IRecordStats, IListArg & { by: string }>({
      queryFn: ({ ws, rt, by, params }) => run(() => dao.records.stats(ws, rt, by, params)),
      providesTags: (_r, _e, { rt }) => [statsTag(rt)],
    }),
    /** Лічильники груп меню «за значеннями поля»: поле → відра статистики (поля з помилкою статистики пропущені). */
    getFacets: b.query<Record<string, IStatBucket[]>, IFacetsArg>({
      queryFn: ({ ws, rt, fields }) => run(() => dao.records.facets(ws, rt, fields)),
      providesTags: (_r, _e, { rt }) => [statsTag(rt)],
    }),
    createRecord: b.mutation<IRecord, { ws: string; rt: string; body: IRecordWrite }>({
      queryFn: ({ ws, rt, body }) => run(() => dao.records.create(ws, rt, body)),
      invalidatesTags: (_r, _e, { rt }) => [listTag(rt), statsTag(rt), 'Recent', myWorkTag],
    }),
    patchRecord: b.mutation<IRecord, IWriteArg>({
      queryFn: ({ ws, rt, id, body, rowVersion }) => run(() => dao.records.patch(ws, rt, id, body, rowVersion)),
      invalidatesTags: (_r, _e, { rt, id }) => [recordTag(rt, id), listTag(rt), statsTag(rt), { type: 'History', id }, { type: 'Links', id: 'LIST' }, myWorkTag],
    }),
    transitionRecord: b.mutation<IRecord, IIdArg & { body: ITransitionRequest }>({
      queryFn: ({ ws, rt, id, body }) => run(() => dao.records.transition(ws, rt, id, body)),
      invalidatesTags: (_r, _e, { rt, id }) => [recordTag(rt, id), listTag(rt), statsTag(rt), { type: 'History', id }, { type: 'Links', id: 'LIST' }, myWorkTag],
    }),
    rankRecord: b.mutation<IRecord, IIdArg & { body: IRankRequest }>({
      queryFn: ({ ws, rt, id, body }) => run(() => dao.records.rank(ws, rt, id, body)),
      invalidatesTags: (_r, _e, { rt }) => [listTag(rt), statsTag(rt)],
    }),
    deleteRecord: b.mutation<void, IIdArg>({
      queryFn: ({ ws, rt, id }) => run(() => dao.records.remove(ws, rt, id)),
      invalidatesTags: (_r, _e, { rt, id }) => [recordTag(rt, id), listTag(rt), statsTag(rt), { type: 'Links', id: 'LIST' }, myWorkTag],
    }),
    bulk: b.mutation<IBulkResult, { ws: string; rt: string; body: IBulkRequest }>({
      queryFn: ({ ws, rt, body }) => run(() => dao.records.bulk(ws, rt, body)),
      invalidatesTags: (_r, _e, { rt }) => [listTag(rt), statsTag(rt), { type: 'Links', id: 'LIST' }, myWorkTag],
    }),
  }),
});

export const {
  useListRecordsQuery, useListAllRecordsQuery, useRecordTitlesQuery, useRecordTypeLinksQuery, useGetRecordQuery, useGetStatsQuery, useGetFacetsQuery, useCreateRecordMutation,
  usePatchRecordMutation, useTransitionRecordMutation, useRankRecordMutation, useDeleteRecordMutation, useBulkMutation,
} = recordsApi;
